import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import { X } from "lucide-react";

const LightboxContext = React.createContext(null);

// Standalone Lightbox component that renders via portal
const Lightbox = ({ image, onClose }) => {
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleOverlayClick = (e) => {
    // Close when clicking on the overlay background
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleCloseClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  // Use portal to render completely outside React tree
  return ReactDOM.createPortal(
    <div 
      className="lightbox-overlay"
      onClick={handleOverlayClick}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      data-testid="lightbox-overlay"
    >
      <button
        className="lightbox-close-btn"
        onClick={handleCloseClick}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        type="button"
        data-testid="lightbox-close"
        aria-label="Close lightbox"
      >
        <X className="w-8 h-8" />
      </button>
      <img 
        src={image} 
        alt="Service photo" 
        className="lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
    </div>,
    document.body
  );
};

export const LightboxProvider = ({ children }) => {
  const [lightboxImage, setLightboxImage] = useState(null);
  
  const closeLightbox = useCallback(() => {
    setLightboxImage(null);
  }, []);
  
  return (
    <LightboxContext.Provider value={{ lightboxImage, setLightboxImage }}>
      {children}
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={closeLightbox} />
      )}
    </LightboxContext.Provider>
  );
};

export const useLightbox = () => React.useContext(LightboxContext);
